import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ReorderModal from "../components/ReorderModal";
import ManageFilesModal from "../components/ManageFilesModal";
import QrCodeDownloadModal from "../components/QrCodeDownloadModal";
import QRCodeWithLogo from "../components/QRCodeLogo";
import { toast } from "react-toastify";
import {
  IoSearchOutline,
  IoFunnelOutline,
  IoChevronDownOutline,
  IoChevronForward,
  IoCheckmark,
  IoCloseCircleOutline,
  IoSwapVerticalOutline,
  IoArrowUpOutline,
  IoArrowDownOutline,
} from "react-icons/io5";
import * as api from "../api";

function CollectionListPage() {
  // Estados para a lista de coleções e UI
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [filterPopoverPos, setFilterPopoverPos] = useState({ top: 0, left: 0 });
  const filterButtonRef = useRef(null);

  // Estados para Modais
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [newName, setNewName] = useState("");
  const [newPageTitle, setNewPageTitle] = useState("");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingCollection, setDeletingCollection] = useState(null);

  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [reorderingCollectionId, setReorderingCollectionId] = useState(null);

  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [managingCollectionId, setManagingCollectionId] = useState(null);

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadingCollection, setDownloadingCollection] = useState(null);

  const fetchCollections = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await api.getCollections();
      setCollections(response.data);
    } catch (err) {
      console.error("Falha ao buscar coleções:", err);
      setError(
        "Não foi possível conectar ao servidor. Verifique se o backend está rodando."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleEditClick = (collection) => {
    setEditingCollection(collection);
    setNewName(collection.collection_name);
    setNewPageTitle(collection.page_title || collection.collection_name);
    setIsEditModalOpen(true);
  };

  const handleUpdateCollection = async (e) => {
    e.preventDefault();
    if (newName && newName.trim() !== "" && editingCollection) {
      try {
        await api.updateCollection(editingCollection.collection_id, {
          newName: newName.trim(),
          newPageTitle: newPageTitle.trim() || newName.trim(),
        });
        fetchCollections();
        setIsEditModalOpen(false);
        setEditingCollection(null);
        toast.success("Coleção atualizada com sucesso!");
      } catch (err) {
        toast.error("Não foi possível atualizar a coleção.");
      }
    }
  };

  const handleReorderClick = (collection) => {
    setReorderingCollectionId(collection.collection_id);
    setIsReorderModalOpen(true);
  };

  const handleSaveOrder = async (collectionId, ordered_filenames) => {
    try {
      await api.reorderPdfs(collectionId, ordered_filenames);
      setIsReorderModalOpen(false);
      toast.success("Ordem salva com sucesso!");
    } catch (err) {
      toast.error("Não foi possível salvar a nova ordem.");
    }
  };

  const handleManageClick = (collection) => {
    setManagingCollectionId(collection.collection_id);
    setIsManageModalOpen(true);
  };

  const handleFilesChanged = () => {
    fetchCollections();
  };

  const toggleFilterPopover = () => {
    if (isFilterOpen) {
      setIsFilterOpen(false);
      setIsSubmenuOpen(false);
      return;
    }
    const rect = filterButtonRef.current?.getBoundingClientRect();
    setFilterPopoverPos({
      top: (rect?.bottom ?? 0) + 4,
      left: rect?.left ?? 0,
    });
    setIsFilterOpen(true);
  };

  const applySort = (value) => {
    setSortOrder(value);
    setIsFilterOpen(false);
    setIsSubmenuOpen(false);
  };

  const cycleNameSort = () => {
    setSortOrder((prev) =>
      prev === "az" ? "za" : prev === "za" ? "" : "az"
    );
  };

  const applyDate = (value) => {
    setSelectedDate(value);
    setIsFilterOpen(false);
    setIsSubmenuOpen(false);
  };

  const getEpoch = (col) => {
    const t = parseInt(String(col.collection_id).split("_")[0], 10);
    return Number.isFinite(t) ? t : 0;
  };

  const availableDates = [
    ...new Set(
      collections
        .map((c) => (c.created_at ? c.created_at.split(" ")[0] : null))
        .filter(Boolean)
    ),
  ].sort((a, b) => {
    const [ad, am, ay] = a.split("/").map(Number);
    const [bd, bm, by] = b.split("/").map(Number);
    return by - ay || bm - am || bd - ad;
  });

  const filteredCollections = collections
    .filter((col) =>
      col.collection_name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((col) => {
      if (!selectedDate) return true;
      if (!col.created_at) return false;
      return col.created_at.split(" ")[0] === selectedDate;
    })
    .sort((a, b) => {
      if (!sortOrder) return 0;
      if (sortOrder === "az" || sortOrder === "za") {
        const cmp = a.collection_name.localeCompare(
          b.collection_name,
          "pt",
          { sensitivity: "base" }
        );
        return sortOrder === "az" ? cmp : -cmp;
      }
      const diff = getEpoch(a) - getEpoch(b);
      return sortOrder === "asc" ? diff : -diff;
    });

  const handleDeleteClick = (collection) => {
    setDeletingCollection(collection);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (deletingCollection) {
      try {
        await api.deleteCollection(deletingCollection.collection_id);
        fetchCollections();
        toast.success(
          `Coleção "${deletingCollection.collection_name}" deletada.`
        );
      } catch (err) {
        toast.error("Não foi possível deletar a coleção.");
      } finally {
        setIsDeleteModalOpen(false);
        setDeletingCollection(null);
      }
    }
  };

  const openDownloadModal = (collection) => {
    setDownloadingCollection(collection);
    setIsDownloadModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="text-gray-600 text-center p-10">
        A carregar coleções...
      </div>
    );
  }

  return (
    <div>
      <ReorderModal
        isOpen={isReorderModalOpen}
        onClose={() => setIsReorderModalOpen(false)}
        onSave={handleSaveOrder}
        collectionId={reorderingCollectionId}
      />
      <ManageFilesModal
        isOpen={isManageModalOpen}
        onClose={() => setIsManageModalOpen(false)}
        onFilesChanged={handleFilesChanged}
        collectionId={managingCollectionId}
      />

      {/* Popover do Filtro e Submenu Estilo SharePoint */}
      {isFilterOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setIsFilterOpen(false);
              setIsSubmenuOpen(false);
            }}
          />
          <div
            className="fixed z-50 bg-white rounded-sm shadow-xl border border-gray-200 w-60 py-1.5 text-sm text-gray-800 font-sans"
            style={{
              top: filterPopoverPos.top,
              left: filterPopoverPos.left,
            }}
          >
            <button
              onClick={() => applySort("asc")}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700"
            >
              <span>Mais antiga a mais recente</span>
              {sortOrder === "asc" && <IoCheckmark className="text-blue-600 text-lg" />}
            </button>

            <button
              onClick={() => applySort("desc")}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between text-gray-700"
            >
              <span>Mais recente a mais antiga</span>
              {sortOrder === "desc" && <IoCheckmark className="text-blue-600 text-lg" />}
            </button>

            <div className="border-t border-gray-200 my-1"></div>

            <div
              className="relative"
              onMouseEnter={() => setIsSubmenuOpen(true)}
            >
              <button
                onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between transition ${
                  selectedDate ? "font-semibold text-blue-600" : "text-gray-700"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>Filtrar por</span>
                  {selectedDate && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-normal">
                      {selectedDate}
                    </span>
                  )}
                </div>
                <IoChevronForward className="text-gray-400 text-xs" />
              </button>

              {isSubmenuOpen && (
                <div className="absolute left-full top-0 ml-0.5 bg-white rounded-sm shadow-xl border border-gray-200 w-48 py-1.5 max-h-60 overflow-y-auto z-50">
                  <button
                    onClick={() => applyDate("")}
                    className="w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center justify-between text-xs text-gray-700"
                  >
                    <span>Todas as datas</span>
                    {!selectedDate && <IoCheckmark className="text-blue-600 text-base" />}
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  {availableDates.length === 0 && (
                    <p className="text-xs text-gray-400 px-3 py-1.5">
                      Sem datas disponíveis.
                    </p>
                  )}
                  {availableDates.map((d) => (
                    <button
                      key={d}
                      onClick={() => applyDate(d)}
                      className="w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center justify-between text-xs text-gray-700"
                    >
                      <span>{d}</span>
                      {selectedDate === d && (
                        <IoCheckmark className="text-blue-600 text-base" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modais */}
      {isDownloadModalOpen && downloadingCollection && (
        <QrCodeDownloadModal
          collectionId={downloadingCollection.collection_id}
          collectionName={downloadingCollection.collection_name}
          qrColor={downloadingCollection.qr_color}
          onClose={() => setIsDownloadModalOpen(false)}
        />
      )}

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
            <h3 className="text-2xl font-bold mb-6 text-gray-900">
              Editar Nome da Coleção
            </h3>
            <form onSubmit={handleUpdateCollection}>
              <div>
                <label
                  htmlFor="new_collection_name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Novo Nome
                </label>
                <input
                  type="text"
                  id="new_collection_name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg p-3 text-gray-900"
                  required
                  autoFocus
                />
              </div>
              <div className="mt-4">
                <label
                  htmlFor="new_page_title"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Título da Página
                </label>
                <input
                  type="text"
                  id="new_page_title"
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg p-3 text-gray-900"
                  placeholder="Se vazio, usa o nome da coleção"
                />
              </div>
              <div className="mt-8 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">
              Confirmar Exclusão
            </h3>
            <p className="text-gray-600 mb-8">
              Tem certeza que deseja excluir a coleção{" "}
              <strong className="text-red-600">
                {deletingCollection?.collection_name}
              </strong>
              ?
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-6 rounded-lg transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg transition"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cabeçalho */}
      <h1 className="text-4xl font-bold text-gray-800 mb-8">Minhas Coleções</h1>

      {/* Barra de Pesquisa Refatorada (UI/UX) */}
      <div className="relative mb-6 max-w-md">
        <div className="relative flex items-center bg-white border border-gray-300 rounded-lg shadow-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all duration-200">
          <IoSearchOutline
            className={`ml-3.5 text-lg transition-colors duration-200 ${
              searchTerm ? "text-blue-600" : "text-gray-400"
            }`}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar coleções por nome."
            className="w-full bg-transparent pl-3 pr-9 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 text-gray-400 hover:text-gray-600 transition"
              title="Limpar pesquisa"
            >
              <IoCloseCircleOutline className="text-lg" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-8 text-center">
          {error}
        </div>
      )}

      {filteredCollections.length > 0 ? (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-4 text-sm font-semibold text-gray-600">
                  QR Code
                </th>
                <th className="p-4 text-sm font-semibold text-gray-600">
                  <span className="flex items-center gap-1.5">
                    Nome da Coleção
                    <button
                      onClick={cycleNameSort}
                      title={
                        sortOrder === "az"
                          ? "Ordenado de A a Z (clique para Z a A)"
                          : sortOrder === "za"
                          ? "Ordenado de Z a A (clique para voltar ao padrão)"
                          : "Ordenar alfabeticamente (A a Z)"
                      }
                      aria-label="Ordenar por nome da coleção"
                      className={`flex items-center gap-1 text-xs font-normal border rounded-md px-2 py-1 transition ${
                        sortOrder === "az" || sortOrder === "za"
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                      }`}
                    >
                      {sortOrder === "az" && <IoArrowDownOutline />}
                      {sortOrder === "za" && <IoArrowUpOutline />}
                      {sortOrder !== "az" && sortOrder !== "za" && (
                        <IoSwapVerticalOutline />
                      )}
                      {(sortOrder === "az" || sortOrder === "za") && (
                        <span>{sortOrder === "az" ? "A-Z" : "Z-A"}</span>
                      )}
                    </button>
                  </span>
                </th>
                <th className="p-4 text-sm font-semibold text-gray-600">
                  <span className="flex items-center gap-1.5">
                    Data de Criação
                    <button
                      ref={filterButtonRef}
                      onClick={toggleFilterPopover}
                      className={`flex items-center gap-1 text-xs font-normal border rounded-md px-2 py-1 transition ${
                        sortOrder === "asc" ||
                        sortOrder === "desc" ||
                        selectedDate
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <IoFunnelOutline />
                      {selectedDate || "Filtrar"}
                      <IoChevronDownOutline />
                    </button>
                  </span>
                </th>
                <th className="p-4 text-sm font-semibold text-gray-600 text-right">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCollections.map((col) => {
                const qrCodeUrl = `${window.location.origin}/collection/${col.collection_id}`;
                return (
                  <tr
                    key={col.collection_id}
                    className="hover:bg-gray-50 transition duration-200"
                  >
                    <td className="p-4">
                      <div className="bg-white p-1 rounded-md inline-block border">
                        <QRCodeWithLogo
                          text={qrCodeUrl}
                          width={80}
                          qrColor={col.qr_color}
                        />
                      </div>
                    </td>
                    <td className="p-4 font-medium text-gray-900 max-w-xs break-words">
                      <Link
                        to={`/collection/${col.collection_id}`}
                        className="text-blue-600 hover:underline"
                        title={col.collection_name}
                      >
                        {col.collection_name}
                      </Link>
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      {col.created_at || "-"}
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <button
                        onClick={() => openDownloadModal(col)}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold py-2 px-3 rounded-md"
                      >
                        Download
                      </button>
                      <button
                        onClick={() => handleEditClick(col)}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold py-2 px-3 rounded-md"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleReorderClick(col)}
                        className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold py-2 px-3 rounded-md"
                      >
                        Reordenar
                      </button>
                      <button
                        onClick={() => handleManageClick(col)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-bold py-2 px-3 rounded-md"
                      >
                        Gerenciar Arquivos
                      </button>
                      <button
                        onClick={() => handleDeleteClick(col)}
                        className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold py-2 px-3 rounded-md"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        !error &&
        (collections.length > 0 ? (
          <div className="bg-white p-6 rounded-xl text-center text-gray-500 shadow-md">
            Nenhuma coleção corresponde aos filtros aplicados.
          </div>
        ) : (
          <div className="bg-white p-6 rounded-xl text-center text-gray-500 shadow-md">
            Nenhuma coleção encontrada.
          </div>
        ))
      )}
    </div>
  );
}

export default CollectionListPage;